import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import PageShell from '@/components/feature/PageShell';
import { categories } from '@/mocks/home';

interface CategoryDetail {
  title: string;
  subtitle: string;
  image: string;
  paragraphs: string[];
  ctaUrl: string;
  ctaLabel: string;
  icon: string;
  relatedSlugs: string[];
}

const categoryDetails: Record<string, CategoryDetail> = {
  clothing: {
    title: 'AllChinaBuy Clothing Spreadsheet',
    subtitle: 'Streetwear, casual wear, and designer-inspired pieces from verified sellers',
    image: 'https://readdy.ai/api/search-image?query=Bright%20minimalist%20boutique%20interior%20with%20organized%20clothing%20rack%20white%20walls%20and%20green%20plants%20wide%20editorial%20shot%20soft%20daylight%20clean%20airy%20retail%20aesthetic%20high%20detail%20fashion%20merchandising&width=1200&height=500&seq=cat-clothing-hero&orientation=landscape',
    paragraphs: [
      'The AllChinaBuy clothing spreadsheet is one of the most visited resources on the platform. It compiles listings from trusted sellers who specialize in streetwear, casual fashion, and designer-inspired apparel. Each entry includes detailed product descriptions, sizing charts, and real buyer QC photos so you know exactly what to expect before ordering.',
      'You can filter by garment type — hoodies, jackets, t-shirts, pants, shorts, underwear, and more — or by seller rating, price range, and brand style. The spreadsheet is updated regularly as sellers release new seasonal collections and restock popular items. This means you are always looking at the latest catalog rather than outdated listings.',
      'Whether you are building a capsule wardrobe or hunting for a specific drop, the clothing spreadsheet saves hours of research by centralizing everything in one place. Compare multiple sellers for the same item, check recent reviews, and shop with confidence knowing every seller has been vetted by the community.'
    ],
    ctaUrl: 'https://kaspreadsheet.com/category-911-Shoes.html',
    ctaLabel: 'Browse Clothing Spreadsheet',
    icon: 'ri-shirt-line',
    relatedSlugs: ['shoes', 'fashion', 'accessories'],
  },
  shoes: {
    title: 'AllChinaBuy Shoes Spreadsheet',
    subtitle: 'Sneakers, boots, sandals, and more from verified sellers with QC details',
    image: 'https://readdy.ai/api/search-image?query=Single%20luxury%20white%20leather%20sneaker%20on%20a%20minimal%20stone%20pedestal%20with%20soft%20grey%20studio%20backdrop%20high%20end%20product%20photography%20elegant%20shadows%20premium%20minimalist%20aesthetic%20high%20detail&width=1200&height=500&seq=cat-shoes-hero&orientation=landscape',
    paragraphs: [
      'The shoes spreadsheet on AllChinaBuy covers every style you can imagine — from everyday sneakers and running shoes to designer boots, sandals, and specialty footwear. Each listing is curated from sellers who have been verified by the community for quality, accuracy, and reliable shipping.',
      'What makes the shoes spreadsheet especially useful is the depth of QC information available. Most listings include side-by-side comparison photos, material close-ups, and on-foot shots from previous buyers. This level of detail helps you understand sizing, comfort, and overall build quality before committing to a purchase.',
      'You can filter by shoe type, brand style, size availability, price bracket, and seller rating. New drops are added frequently, so checking back regularly is the best way to catch limited releases and restocks from your favorite sellers. The spreadsheet also highlights sellers known for specific categories — like high-quality replicas or budget-friendly alternatives.'
    ],
    ctaUrl: 'https://kaspreadsheet.com/category-911-Shoes.html',
    ctaLabel: 'Browse Shoes Spreadsheet',
    icon: 'ri-footprint-line',
    relatedSlugs: ['clothing', 'fashion', 'accessories'],
  },
  fashion: {
    title: 'AllChinaBuy Fashion Spreadsheet',
    subtitle: 'Trending fashion items, curated collections, and seasonal style guides',
    image: 'https://readdy.ai/api/search-image?query=Creative%20fashion%20moodboard%20with%20magazine%20cutouts%20fabric%20swatches%20and%20color%20palette%20cards%20arranged%20on%20a%20designer%20desk%20artistic%20studio%20flatlay%20vibrant%20editorial%20composition%20high%20detail&width=1200&height=500&seq=cat-fashion-hero&orientation=landscape',
    paragraphs: [
      'The fashion spreadsheet goes beyond basic clothing to cover curated collections, seasonal picks, and complete style guides that help you build a cohesive wardrobe. It features trend reports, outfit inspiration, and seller recommendations organized by aesthetic — from minimalist basics to bold streetwear statements.',
      'Each collection is hand-picked by community contributors and experienced buyers who understand what works in real life. Listings include styling notes, fabric recommendations, and fit advice so you can plan purchases that complement pieces you already own. This turns spreadsheet browsing into a genuine style resource.',
      'Seasonal updates mean you will find summer essentials, winter layering pieces, and transitional items exactly when you need them. The fashion spreadsheet also highlights collaborative drops, limited-edition pieces, and seller exclusives that you might otherwise miss scrolling through generic listings.'
    ],
    ctaUrl: 'https://kaspreadsheet.com',
    ctaLabel: 'Browse Fashion Spreadsheet',
    icon: 'ri-t-shirt-2-line',
    relatedSlugs: ['clothing', 'shoes', 'accessories'],
  },
  electronics: {
    title: 'AllChinaBuy Electronics Spreadsheet',
    subtitle: 'Gadgets, tech accessories, and everyday devices from trusted sellers',
    image: 'https://readdy.ai/api/search-image?query=Smartphone%20wireless%20earbuds%20and%20smartwatch%20on%20a%20clean%20white%20desk%20with%20geometric%20light%20and%20shadow%20patterns%20modern%20tech%20product%20photography%20minimal%20premium%20aesthetic%20high%20detail&width=1200&height=500&seq=cat-electronics-hero&orientation=landscape',
    paragraphs: [
      'The electronics section of the AllChinaBuy spreadsheet covers a wide range of tech products — from smartphones and wireless earbuds to smartwatches, charging accessories, and everyday gadgets. Each listing is sourced from sellers who have proven track records for accurate product descriptions and reliable delivery.',
      'Tech purchases require extra caution, which is why the electronics spreadsheet emphasizes detailed QC documentation. Buyers share unboxing photos, functionality tests, and comparison shots against retail versions when applicable. This transparency helps you avoid common pitfalls like incorrect specs or missing accessories.',
      'Filter by product category, brand compatibility, price range, and seller rating to narrow down exactly what you need. The spreadsheet also tracks which sellers offer warranty coverage, replacement guarantees, and responsive after-sales support — critical factors when buying electronics from overseas sellers.'
    ],
    ctaUrl: 'https://kaspreadsheet.com',
    ctaLabel: 'Browse Electronics Spreadsheet',
    icon: 'ri-smartphone-line',
    relatedSlugs: ['accessories', 'clothing', 'shoes'],
  },
  accessories: {
    title: 'AllChinaBuy Accessories Spreadsheet',
    subtitle: 'Belts, bags, jewelry, watches, and more through verified sellers',
    image: 'https://readdy.ai/api/search-image?query=Luxury%20wristwatch%20leather%20wallet%20and%20sunglasses%20inside%20an%20elegant%20gift%20box%20with%20silk%20lining%20premium%20product%20photography%20warm%20sophisticated%20lighting%20high%20detail%20reflective%20surfaces&width=1200&height=500&seq=cat-accessories-hero&orientation=landscape',
    paragraphs: [
      'Accessories can make or break an outfit, and the AllChinaBuy accessories spreadsheet helps you find the perfect finishing touches. From leather belts and designer bags to sunglasses, watches, and jewelry, this section covers every category of accessory with detailed seller and product information.',
      'The accessories spreadsheet is particularly valuable because these items are often harder to judge from photos alone. Community QC photos show material quality, hardware details, stitching accuracy, and packaging — all the small details that separate great accessories from disappointing ones.',
      'You can browse by accessory type, material, price point, or seller specialty. Many sellers in this category focus exclusively on one type of product, which means they have deeper expertise and better quality control than generalist sellers. The spreadsheet highlights these specialist sellers so you can shop with confidence.'
    ],
    ctaUrl: 'https://kaspreadsheet.com/search-920/name/Accessories.html',
    ctaLabel: 'Browse Accessories Spreadsheet',
    icon: 'ri-handbag-line',
    relatedSlugs: ['fashion', 'clothing', 'shoes'],
  },
  'trusted-sellers': {
    title: 'AllChinaBuy Trusted Sellers',
    subtitle: 'Verified seller reviews, ratings, and product specialties to shop with confidence',
    image: 'https://readdy.ai/api/search-image?query=Golden%20shield%20badge%20with%20a%20checkmark%20floating%20on%20a%20deep%20green%20gradient%20background%20trust%20and%20security%20concept%20professional%203D%20render%20clean%20minimal%20composition%20high%20detail&width=1200&height=500&seq=cat-sellers-hero&orientation=landscape',
    paragraphs: [
      'The trusted sellers spreadsheet is the foundation of safe shopping on AllChinaBuy. It contains a curated directory of sellers who have been verified by the community through real transactions, consistent quality, and reliable communication. Each seller profile includes a rating, review summary, and known product specialties.',
      'What sets trusted sellers apart is their track record. These sellers have processed hundreds or thousands of orders with minimal disputes, accurate product descriptions, and fair handling of QC issues. The spreadsheet tracks key metrics like average shipping time, QC photo quality, return policy, and communication responsiveness.',
      'New sellers are added only after passing a community review period, and underperforming sellers are flagged or removed. This ongoing curation means the trusted seller list is a living document that reflects the current state of the marketplace, not a static directory that grows stale over time.'
    ],
    ctaUrl: 'https://kaspreadsheet.com',
    ctaLabel: 'Browse Trusted Sellers',
    icon: 'ri-shield-check-line',
    relatedSlugs: ['clothing', 'shoes', 'accessories'],
  },
};

const relatedMeta: Record<string, { label: string; icon: string }> = {
  clothing: { label: 'Clothing', icon: 'ri-shirt-line' },
  shoes: { label: 'Shoes', icon: 'ri-footprint-line' },
  fashion: { label: 'Fashion', icon: 'ri-t-shirt-2-line' },
  electronics: { label: 'Electronics', icon: 'ri-smartphone-line' },
  accessories: { label: 'Accessories', icon: 'ri-handbag-line' },
  'trusted-sellers': { label: 'Trusted Sellers', icon: 'ri-shield-check-line' },
};

export default function SpreadsheetPage() {
  const { slug } = useParams<{ slug?: string }>();
  const { t } = useTranslation('pages');
  const navigate = useNavigate();

  // If a category slug is present, render the category detail page
  if (slug && categoryDetails[slug]) {
    const detail = categoryDetails[slug];
    const currentLang = window.location.pathname.split('/')[1] || 'en';

    return (
      <PageShell
        eyebrow="Category"
        title={detail.title}
        intro={detail.subtitle}
      >
        <div className="space-y-10">
          {/* Hero image */}
          <div className="relative h-56 md:h-72 rounded-xl overflow-hidden">
            <img
              src={detail.image}
              alt={detail.title}
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-foreground-900/50 via-foreground-900/10 to-transparent" />
            <div className="absolute bottom-4 left-5 md:bottom-6 md:left-6 flex items-center gap-3">
              <div className="w-10 h-10 md:w-12 md:h-12 rounded-full bg-background-50/20 backdrop-blur-sm flex items-center justify-center">
                <i className={`${detail.icon} text-background-50 text-lg md:text-xl`}></i>
              </div>
              <span className="text-background-50 font-heading text-lg md:text-xl font-semibold">
                {detail.title}
              </span>
            </div>
          </div>

          {/* Content paragraphs */}
          <div className="prose-content space-y-6">
            {detail.paragraphs.map((p, i) => (
              <p key={i} className="text-foreground-600 text-base leading-relaxed">
                {p}
              </p>
            ))}
          </div>

          {/* CTA */}
          <div className="flex flex-col sm:flex-row gap-4">
            <a
              href={detail.ctaUrl}
              target="_blank"
              rel="noopener nofollow"
              className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-primary-500 text-background-50 rounded-lg font-medium hover:bg-primary-600 transition-colors"
            >
              {detail.ctaLabel}
              <i className="ri-external-link-line"></i>
            </a>
            <button
              onClick={() => navigate(`/${currentLang}/allchinabuy-spreadsheet/`)}
              className="inline-flex items-center justify-center gap-2 px-6 py-3 border border-background-200/60 text-foreground-700 rounded-lg font-medium hover:bg-background-100 transition-colors"
            >
              <i className="ri-arrow-left-line"></i>
              Back to Spreadsheet Hub
            </button>
          </div>

          {/* Related categories */}
          <section>
            <h3 className="font-heading text-lg md:text-xl font-semibold text-foreground-900 mb-4">
              Related Categories
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              {detail.relatedSlugs.map((rs) => {
                const meta = relatedMeta[rs];
                if (!meta) return null;
                return (
                  <button
                    key={rs}
                    onClick={() => navigate(`/${currentLang}/allchinabuy-spreadsheet/${rs}/`)}
                    className="flex items-center gap-3 p-4 bg-background-100 rounded-lg border border-background-200/50 hover:border-primary-300/60 hover:bg-primary-50 transition-colors text-left"
                  >
                    <div className="w-10 h-10 rounded-full bg-primary-100 flex items-center justify-center shrink-0">
                      <i className={`${meta.icon} text-primary-600`}></i>
                    </div>
                    <div>
                      <span className="block font-medium text-foreground-800">{meta.label}</span>
                      <span className="text-xs text-foreground-400">Explore category</span>
                    </div>
                    <i className="ri-arrow-right-line text-foreground-400 ml-auto"></i>
                  </button>
                );
              })}
            </div>
          </section>
        </div>
      </PageShell>
    );
  }

  // Generic Spreadsheet Hub page (no slug)
  return (
    <PageShell
      eyebrow={t('spreadsheet_eyebrow')}
      title={t('spreadsheet_h1')}
      intro={t('spreadsheet_intro')}
    >
      <div className="prose-content space-y-10">
        <section>
          <h2 className="font-heading text-xl md:text-2xl font-semibold text-foreground-900 mb-4">
            {t('spreadsheet_s1_title')}
          </h2>
          <p className="text-foreground-600 text-base leading-relaxed">{t('spreadsheet_s1_body')}</p>
        </section>
        <section>
          <h2 className="font-heading text-xl md:text-2xl font-semibold text-foreground-900 mb-4">
            {t('spreadsheet_s2_title')}
          </h2>
          <p className="text-foreground-600 text-base leading-relaxed">{t('spreadsheet_s2_body')}</p>
        </section>
        <section>
          <h2 className="font-heading text-xl md:text-2xl font-semibold text-foreground-900 mb-4">
            {t('spreadsheet_s3_title')}
          </h2>
          <p className="text-foreground-600 text-base leading-relaxed">{t('spreadsheet_s3_body')}</p>
        </section>

        {/* Category grid linking to main site */}
        <section>
          <h2 className="font-heading text-xl md:text-2xl font-semibold text-foreground-900 mb-5">
            {t('spreadsheet_categories_title')}
          </h2>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
            {categories.map((cat) => (
              <a
                key={cat.name}
                href={cat.url}
                target="_blank"
                rel="noopener nofollow"
                className="flex items-center gap-2 px-4 py-3 bg-background-100 rounded-lg border border-background-200/50 hover:border-primary-300/60 hover:bg-primary-50 transition-colors group"
              >
                <i className="ri-shopping-bag-3-line text-primary-400 group-hover:text-primary-600 transition-colors"></i>
                <span className="text-sm font-medium text-foreground-700 group-hover:text-primary-700 transition-colors">
                  {t(cat.name, { ns: 'home' })}
                </span>
              </a>
            ))}
          </div>
        </section>
      </div>
    </PageShell>
  );
}